import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Mail, MapPin, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const { language, t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t.contact.form.success);
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t.contact.form.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative section-padding bg-white overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-full h-1/2 bg-[#F8FAFC]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 ${language === 'ar' ? 'text-right' : ''}`}>
          <span className="inline-block px-4 py-1.5 bg-[#0B4DA2]/10 text-[#0B4DA2] rounded-full text-sm font-semibold mb-4">
            {t.contact.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900">
            {t.contact.title}
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <div className={`space-y-8 ${language === 'ar' ? 'lg:order-2 text-right' : ''}`}>
            {/* Email Card */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
              <div className={`flex items-center gap-4 mb-4 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                <div className="w-12 h-12 bg-[#0B4DA2]/10 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#0B4DA2]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">{t.contact.email}</h3>
              </div>
              <a
                href="mailto:info@at33d.com"
                className="text-xl font-medium text-[#0B4DA2] hover:text-[#00A8E8] transition-colors"
                data-testid="contact-email"
              >
                info@at33d.com
              </a>
            </div>

            {/* Location Info */}
            <div className="bg-gradient-to-br from-[#0B4DA2] to-[#00A8E8] rounded-2xl p-8 text-white">
              <div className={`flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}><MapPin className="w-6 h-6" /><p className="text-white/90 leading-relaxed">{t.contact.location}</p></div>
            </div>
          </div>

          {/* Contact Form */}
          <div className={`bg-white rounded-2xl p-8 shadow-lg border border-slate-100 ${language === 'ar' ? 'lg:order-1' : ''}`}>
            <h3 className={`text-xl font-bold text-slate-900 mb-6 ${language === 'ar' ? 'text-right' : ''}`}>
              {t.contact.sendMessage}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="contact-name" className={`text-slate-700 font-medium ${language === 'ar' ? 'text-right block' : ''}`}>
                  {t.contact.form.name}
                </Label>
                <Input
                  id="contact-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="h-12 border-slate-200 focus:border-[#0B4DA2] focus:ring-[#0B4DA2]"
                  data-testid="contact-input-name"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="contact-email" className={`text-slate-700 font-medium ${language === 'ar' ? 'text-right block' : ''}`}>
                  {t.contact.form.email}
                </Label>
                <Input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-12 border-slate-200 focus:border-[#0B4DA2] focus:ring-[#0B4DA2]"
                  data-testid="contact-input-email"
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="contact-message" className={`text-slate-700 font-medium ${language === 'ar' ? 'text-right block' : ''}`}>
                  {t.contact.form.message}
                </Label>
                <Textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="border-slate-200 focus:border-[#0B4DA2] focus:ring-[#0B4DA2] resize-none"
                  data-testid="contact-input-message"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#0B4DA2] hover:bg-[#093C80] text-white py-6 rounded-full font-semibold transition-all duration-300 hover:shadow-lg disabled:opacity-70 relative z-10"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⏳</span>
                    {t.contact.sending}
                  </span>
                ) : (
                  <span className={`flex items-center justify-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                    {t.contact.form.submit}
                    <Send className={`w-5 h-5 ${language === 'ar' ? 'rotate-180' : ''}`} />
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
